import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	BasicUserWithPermissionRolesRes,
	CreateDealDto,
	DealBasicDataTct,
	ProjectRepresentativeUserEntityRes,
	SummaryDealExtendedTct,
	SummaryDealTct,
	UpdateCompanyRepresentativeDto,
	UpdateDealDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';

export interface IDeleteDealArgs {
	dealId: string;
}

export const getUserDealList = async (signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<SummaryDealTct[]>>(
		{
			endpoint: API_PATHS.DEAL_GET,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);

	return response;
};

export const getDealData = async (args: void, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<SummaryDealTct>>(
		{
			endpoint: API_PATHS.DEAL_DATA_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);

	return response;
};

export const getDealBasicData = async (args: void, options?: ServiceRequestOptions) => {
	return makeRequest<ApiV1.Response<DealBasicDataTct>>(
		{
			endpoint: API_PATHS.DEAL_BASIC_DATA_GET,
			method: HTTP_METHOD.GET,
		},
		{
			...options,
		}
	);
};

export const getDealQuery = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<SummaryDealExtendedTct>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	).then((response) => response.payload);
};

export const createDeal = async (args: CreateDealDto, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<SummaryDealTct>>(
		{
			endpoint: API_PATHS.DEAL_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				...args,
			}),
		},
		{
			...options,
		}
	);

	return response;
};

export const updateDeal = async (
	args: { dealId: string } & UpdateDealDto,
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<SummaryDealTct[]>>(
		{
			endpoint: API_PATHS.DEAL_PATCH,
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify({
				...args,
			}),
			customHeaders: {
				'deal-id': args.dealId,
			},
		},
		{
			...options,
		}
	);
};

export const updateDealRestriction = async (
	args: { dealId: string; isRestricted: boolean },
	options?: ServiceRequestOptions
) => {
	await makeRequest<ApiV1.Response<void>>(
		{
			endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_RESTRICTION_PATCH, {
				dealId: args.dealId,
			}),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify({ isRestricted: args.isRestricted }),
			customHeaders: {
				'deal-id': args.dealId,
			},
		},
		{
			...options,
		}
	);
};

export const deleteDeal = async (args: IDeleteDealArgs, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<string | null>>(
		{
			endpoint: API_PATHS.DEAL_DELETE,
			method: HTTP_METHOD.DELETE,
			customHeaders: {
				'deal-id': args.dealId,
			},
		},
		{
			...options,
		}
	);
	return response;
};

export const getDealUsers = async (
	endpoint: string
): Promise<BasicUserWithPermissionRolesRes[] | undefined> => {
	return makeRequest<ApiV1.Response<BasicUserWithPermissionRolesRes[]>>({
		endpoint,
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const getProjectRepresentative = async (endpoint: string, args: { dealId: string }) => {
	const { dealId } = args;
	return makeRequest<ApiV1.Response<ProjectRepresentativeUserEntityRes | null>>({
		endpoint: formatUnicorn(endpoint, { dealId }),
		method: HTTP_METHOD.GET,
	}).then((response) => response.payload);
};

export const updateProjectRepresentative = async (data: {
	dealId: string;
	body: UpdateCompanyRepresentativeDto;
}) => {
	const { dealId, body } = data;

	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_UPDATE_PROJECT_REPRESENTATIVE_PUT, {
			dealId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(body),
	});
};
